import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import permissionService from '../services/permissionService';
import { PageTitleContainer } from '../components/common';

// Wrapper with page-level styling for sub-pages
const SubPageWrapper = ({ children }) => (
  <Box sx={{
    width: '100%',
    bgcolor: '#f5f6f7',
    minHeight: '100vh',
    pb: 4,
    px: 6,
    pt: 4,
    overflowX: 'hidden',
    backgroundImage: 'none',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  }}>
    {children}
  </Box>
);

const ManagePermissionsView = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, permission: null });

  useEffect(() => { loadPermissions(); }, []);

  const handleDelete = async (force = false) => {
    try {
      await permissionService.deletePermission(deleteDialog.permission.id || deleteDialog.permission.path, force);
      setDeleteDialog({ open: false, permission: null });
      loadPermissions();
    } catch (err) {
      const msg = typeof err === 'string' ? err : (err?.message || 'Failed to delete permission');
      // If assigned to roles, show force delete option
      if (msg.includes('assigned to') && !force) {
        setDeleteDialog(prev => ({ ...prev, showForce: true, errorMsg: msg }));
      } else {
        setError(msg);
        setDeleteDialog({ open: false, permission: null });
      }
    }
  };

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const [systemRes, projectRes] = await Promise.all([
        permissionService.getAllPermissions('system'),
        permissionService.getAllPermissions('project'),
      ]);
      const systemPerms = systemRes?.permissions || systemRes?.data?.permissions || [];
      const projectPerms = projectRes?.permissions || projectRes?.data?.permissions || [];
      setPermissions([...systemPerms, ...projectPerms]);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load permissions');
    }
    setLoading(false);
  };

  const filtered = permissions.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.path.toLowerCase().includes(search.toLowerCase())
  );

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 230px)');
  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);
  return (
    <Box>
      <PageTitleContainer
        title={'Manage permissions'}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Paper sx={{
        borderRadius: '4px',
        p: 5,
        width: '100%',
        height: contentHeight,
        bgcolor: 'white',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        backgroundImage: 'none',
        '&::before': {
          display: 'none'
        },
        '&::after': {
          display: 'none'
        }
      }}>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField size="small" placeholder="Search permissions..." fullWidth value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                color: 'var(--color-blue, #475969)',
                height: '45px',
                transition: 'all 0.2s',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f44336',
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4759694d',
                border: '1px solid #4759694d',
                borderRadius: '4px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                padding: '12px 14px',
                color: 'var(--color-blue, #475969)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a2abb3',
                opacity: 0.7,
                fontSize: '14px',
              },
              '& .MuiFormHelperText-root': {
                marginLeft: '4px',
                fontSize: '12px'
              }
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important" }}>Permission</TableCell>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important" }}>Path</TableCell>
                  <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No permissions found</TableCell></TableRow>
                )}
                {filtered.map(perm => (
                  <TableRow key={perm.path} hover sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white !important'
                    }
                  }}>
                    <TableCell variant="body2">
                      {perm.name}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{perm.path}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" title="Edit" onClick={() => navigate('/permissions/create?edit=' + (perm.id || perm.path))}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" title="Delete" onClick={() => setDeleteDialog({ open: true, permission: perm })}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, permission: null })}>
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          {deleteDialog.showForce ? (
            <>
              <DialogContentText color="error" sx={{ mb: 1 }}>
                {deleteDialog.errorMsg}
              </DialogContentText>
              <DialogContentText>
                Force delete will remove this permission from all assigned roles. Continue?
              </DialogContentText>
            </>
          ) : (
            <DialogContentText>
              Are you sure you want to delete the permission "{deleteDialog.permission?.name}"? This action cannot be undone.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, permission: null })}>Cancel</Button>
          {deleteDialog.showForce ? (
            <Button color="error" variant="contained" onClick={() => handleDelete(true)}>Force Delete</Button>
          ) : (
            <Button color="error" variant="contained" onClick={() => handleDelete(false)}>Delete</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ManagePermissionsPage = () => {
  return <SubPageWrapper><ManagePermissionsView /></SubPageWrapper>;
};

export default ManagePermissionsPage;
export { ManagePermissionsView };
